import { onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { salesDueDiligenceApi } from '@/services/api'
import { useApplicationStore } from '@/config/store/applicationStore'
import { useUserStore } from '@/config/store/userStore'

export type OrbitPendingJob = {
  id: string
  companyId: string
  engagementId?: string | null
  companyDisplay?: string | null
  startedAt: string
}

const STORAGE_PREFIX = 'orbit-pending-jobs'
const POLL_MS = 12_000
const MAX_AGE_MS = 3 * 60 * 60 * 1000
const ORBIT_EVENT = 'orbit-pending-updated'

function storageKeyForUser(userId: string) {
  return `${STORAGE_PREFIX}:${userId}`
}

function getConnectedUserId(): string | null {
  const userStore = useUserStore()
  const user = userStore.user as any
  const id = user?._id || user?.resource?._id || user?.user?._id
  return id ? String(id) : null
}

function readJobs(userId: string): OrbitPendingJob[] {
  try {
    const raw = localStorage.getItem(storageKeyForUser(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  }
  catch {
    return []
  }
}

function writeJobs(userId: string, jobs: OrbitPendingJob[]) {
  localStorage.setItem(storageKeyForUser(userId), JSON.stringify(jobs))
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ORBIT_EVENT))
  }
}

/** Register an Orbit run so a global watcher can notify if the user leaves the page. */
export function trackOrbitJob(job: {
  id: string
  companyId: string
  engagementId?: string | null
  companyDisplay?: string | null
}) {
  const userId = getConnectedUserId()
  if (!userId || !job.id) return

  const jobs = readJobs(userId).filter(j => j.id !== job.id)
  jobs.push({
    id: job.id,
    companyId: job.companyId,
    engagementId: job.engagementId || null,
    companyDisplay: job.companyDisplay || null,
    startedAt: new Date().toISOString(),
  })
  writeJobs(userId, jobs)
}

/** Stop watching a job (completed/failed while still on the Orbit panel). */
export function untrackOrbitJob(jobId: string) {
  const userId = getConnectedUserId()
  if (!userId || !jobId) return
  const next = readJobs(userId).filter(j => j.id !== jobId)
  if (next.length === readJobs(userId).length) return
  writeJobs(userId, next)
}

/** Clear all pending Orbit jobs for the connected user (stuck localStorage recovery). */
export function clearOrbitPendingJobs() {
  const userId = getConnectedUserId()
  if (!userId) return
  writeJobs(userId, [])
}

/**
 * Global Orbit completion watcher — mount once from App.vue.
 * Polls localStorage pending jobs for the connected user and shows a toast on completion.
 */
export function useOrbitCompletionWatch() {
  const userStore = useUserStore()
  const applicationStore = useApplicationStore()
  const { t } = useI18n()

  let pollTimer: ReturnType<typeof setInterval> | null = null
  let pollInFlight = false

  const stopPolling = () => {
    if (pollTimer) {
      clearInterval(pollTimer)
      pollTimer = null
    }
  }

  const orbitRoute = (job: OrbitPendingJob) => {
    if (job.engagementId) {
      return {
        name: 'DealWorkspace' as const,
        params: { engagementId: job.engagementId },
      }
    }
    return `/sales/${job.companyId}`
  }

  const notify = (job: OrbitPendingJob, status: 'completed' | 'failed') => {
    const company = job.companyDisplay || t('modules.salesFunnel.dueDiligence.notifyFallbackCompany')
    const undoTo = orbitRoute(job)

    if (status === 'completed') {
      applicationStore.pushError({
        title: t('modules.salesFunnel.dueDiligence.notifyDoneTitle'),
        message: t('modules.salesFunnel.dueDiligence.notifyDoneMessage', { company }),
        color: 'success',
        duration: 12000,
        undoLabel: t('modules.salesFunnel.dueDiligence.notifyView'),
        undoTo,
      })
      return
    }

    applicationStore.pushError({
      title: t('modules.salesFunnel.dueDiligence.notifyFailedTitle'),
      message: t('modules.salesFunnel.dueDiligence.notifyFailedMessage', { company }),
      color: 'error',
      duration: 10000,
      undoLabel: t('modules.salesFunnel.dueDiligence.notifyView'),
      undoTo,
    })
  }

  const tick = async () => {
    if (pollInFlight) return
    const userId = getConnectedUserId()
    if (!userId) return

    const now = Date.now()
    const previous = readJobs(userId)
    let jobs = previous.filter((job) => {
      const age = now - new Date(job.startedAt).getTime()
      return Number.isFinite(age) && age < MAX_AGE_MS
    })
    if (jobs.length !== previous.length) {
      writeJobs(userId, jobs)
    }
    if (!jobs.length) {
      stopPolling()
      return
    }

    pollInFlight = true
    try {
      const remaining: OrbitPendingJob[] = []
      for (const job of jobs) {
        try {
          const doc = await salesDueDiligenceApi.getById(job.id).promise
          const status = doc?.status
          if (status === 'completed' || status === 'failed') {
            notify(job, status)
            continue
          }
          if (['pending', 'queued', 'running', 'enriching'].includes(status)) {
            remaining.push(job)
          }
          // Unknown / missing status → drop from watch list
        }
        catch (err: any) {
          const code = err?.response?.status ?? err?.statusCode ?? err?.status
          // Gone from DB (deleted / never persisted) — stop polling forever
          if (code === 404 || code === 403) continue
          // Transient network / 5xx — keep watching
          remaining.push(job)
        }
      }
      writeJobs(userId, remaining)
      if (!remaining.length) stopPolling()
    }
    finally {
      pollInFlight = false
    }
  }

  const startPolling = () => {
    const userId = getConnectedUserId()
    if (!userId) {
      stopPolling()
      return
    }
    if (!readJobs(userId).length) {
      stopPolling()
      return
    }
    if (pollTimer) return
    void tick()
    pollTimer = setInterval(() => { void tick() }, POLL_MS)
  }

  const onPendingUpdated = () => startPolling()

  watch(
    () => (userStore.user as any)?._id || (userStore.user as any)?.resource?._id,
    (id) => {
      stopPolling()
      if (id) startPolling()
    },
    { immediate: true },
  )

  if (typeof window !== 'undefined') {
    window.addEventListener(ORBIT_EVENT, onPendingUpdated)
    window.addEventListener('storage', (event) => {
      const userId = getConnectedUserId()
      if (!userId) return
      if (event.key === storageKeyForUser(userId)) startPolling()
    })
  }

  onUnmounted(() => {
    stopPolling()
    if (typeof window !== 'undefined') {
      window.removeEventListener(ORBIT_EVENT, onPendingUpdated)
    }
  })
}
